<template>
    <b-modal
        :visible="visible"
        title="인벤토리 품질 점검"
        size="xl"
        centered
        hide-footer
        body-class="quality-modal-body"
        @hidden="handleHidden"
    >
        <div v-if="loading" class="loading-state">
            <i class="fas fa-spinner fa-spin"></i>
            <span>점검 보고서를 불러오는 중입니다...</span>
        </div>

        <div v-else-if="!result" class="empty-state">
            <i class="fas fa-clipboard-check"></i>
            <p>점검 결과가 없습니다. 먼저 품질 점검을 실행해주세요.</p>
        </div>

        <div v-else class="quality-content">
            <!-- 핵심 정보 영역 (상단 고정) -->
            <section class="hero-section">
                <div class="hero-main-row">
                    <div class="hero-score-card" :class="scoreClass">
                        <div class="hero-score-header">
                            <span class="hero-label">품질 총점</span>
                            <span class="hero-status-badge" :class="statusClass">{{ statusLabel }}</span>
                        </div>
                        <div class="hero-score-value">
                            <strong>{{ result.qualityScore ?? result.overallScore ?? 0 }}</strong>
                            <span class="hero-score-max">/ 100</span>
                        </div>
                        <p class="hero-description">{{ scoreDescription }}</p>
                    </div>

                    <div class="hero-stats-card">
                        <div class="hero-stat-header">
                            <span class="hero-stat-label">점검 현황</span>
                            <span class="hero-stat-count">총 {{ result.totalChecks || 0 }}건</span>
                        </div>
                        <div class="hero-stat-values">
                            <div class="hero-stat-item stat-pass">
                                <strong>{{ result.passedChecks || 0 }}</strong>
                                <span>통과</span>
                            </div>
                            <div class="hero-stat-item stat-warning">
                                <strong>{{ result.warnings || 0 }}</strong>
                                <span>경고</span>
                            </div>
                            <div class="hero-stat-item stat-error">
                                <strong>{{ result.errors || 0 }}</strong>
                                <span>오류</span>
                            </div>
                        </div>
                    </div>

                    <article v-if="result.methodology" class="hero-methodology-card">
                        <span class="hero-stat-label">검증 기준</span>
                        <dl class="hero-methodology-list">
                            <div class="hero-methodology-item">
                                <dt>표준</dt>
                                <dd>{{ result.methodology.standard || "-" }}</dd>
                            </div>
                            <div class="hero-methodology-item">
                                <dt>버전</dt>
                                <dd>{{ result.methodology.version || "-" }}</dd>
                            </div>
                            <div class="hero-methodology-item">
                                <dt>비고</dt>
                                <dd>{{ result.methodology.remarks || "-" }}</dd>
                            </div>
                        </dl>
                    </article>
                </div>

                <!-- 액션 필요 여부 표시 -->
                <div v-if="result.summary?.actionRequired" class="action-alert-inline">
                    <i class="fas fa-exclamation-triangle"></i>
                    <span>조치가 필요한 항목이 있습니다</span>
                </div>
            </section>

            <!-- 스크롤 가능한 상세 정보 영역 -->
            <div class="detail-content">
                <!-- 품질 이슈 요약 -->
                <section v-if="result.summary?.estimatedImpact" class="detail-section compact-section">
                    <div class="section-header">
                        <h5 class="section-title">품질 영향도 평가</h5>
                    </div>
                    <div class="impact-grid">
                        <article class="impact-card">
                            <span class="impact-label">데이터 정확도</span>
                            <strong class="impact-value">{{
                                result.summary.estimatedImpact.dataAccuracy || "-"
                            }}</strong>
                        </article>
                        <article class="impact-card">
                            <span class="impact-label">보고 신뢰도</span>
                            <strong class="impact-value">{{
                                result.summary.estimatedImpact.reportReliability || "-"
                            }}</strong>
                        </article>
                        <article class="impact-card">
                            <span class="impact-label">검증 준비도</span>
                            <strong class="impact-value">{{
                                result.summary.estimatedImpact.verificationReadiness || "-"
                            }}</strong>
                        </article>
                    </div>
                </section>

                <!-- 중점 조치 대상 -->
                <section v-if="actionItems.length" class="detail-section compact-section">
                    <div class="section-header">
                        <h5 class="section-title">중점 조치 대상</h5>
                        <span class="section-count">{{ actionItems.length }}건</span>
                    </div>
                    <div class="action-grid">
                        <article
                            v-for="(item, idx) in actionItems"
                            :key="idx"
                            class="action-card"
                            :class="`priority-${item.priority}`"
                        >
                            <header class="action-card-header">
                                <span class="priority-chip">{{ priorityLabel(item.priority) }}</span>
                                <strong class="action-category">{{ item.name }}</strong>
                            </header>
                            <p class="action-text">{{ item.action }}</p>
                        </article>
                    </div>
                </section>

                <!-- 세부 점검 항목 -->
                <section class="detail-section compact-section">
                    <div class="section-header">
                        <h5 class="section-title">세부 점검 항목</h5>
                        <span class="section-count">{{ (result.checks || []).length }}건</span>
                    </div>
                    <div class="table-wrapper">
                        <table class="table table-sm quality-table">
                            <thead class="carbon-quality-table-header">
                                <tr>
                                    <th class="col-item">항목</th>
                                    <th class="col-status">상태</th>
                                    <th class="col-score">점수</th>
                                    <th class="col-method">방법</th>
                                    <th class="col-detail">비고</th>
                                </tr>
                            </thead>
                            <tbody class="carbon-quality-table-body">
                                <tr v-for="(check, index) in result.checks || []" :key="index">
                                    <td class="col-item">
                                        <span class="table-cell-primary">{{ check.name || check.checkName }}</span>
                                    </td>
                                    <td class="col-status">
                                        <span class="badge" :class="badgeClass(check.status)">{{
                                            badgeLabel(check.status)
                                        }}</span>
                                    </td>
                                    <td class="col-score">
                                        <span class="table-cell-primary">{{ check.score }}</span
                                        ><span class="table-cell-secondary">/{{ check.maxScore || 100 }}</span>
                                    </td>
                                    <td class="col-method">
                                        <span v-if="check.method?.formula" class="table-cell-primary">{{
                                            check.method.formula
                                        }}</span>
                                    </td>
                                    <td class="col-detail">
                                        <span class="table-cell-primary">{{ check.method?.remarks || "-" }}</span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </section>

                <!-- 누락 데이터 상세 -->
                <section v-if="missingDataIssues.length" class="detail-section compact-section">
                    <div class="section-header">
                        <h5 class="section-title">누락 데이터 상세</h5>
                        <span class="section-count">{{ missingDataIssues.length }}건</span>
                    </div>
                    <div class="scroll-pane">
                        <table class="table table-sm table-striped compact-table">
                            <thead class="carbon-quality-table-header">
                                <tr>
                                    <th>시설</th>
                                    <th>기간</th>
                                    <th>카테고리</th>
                                    <th>심각도</th>
                                    <th>사유</th>
                                </tr>
                            </thead>
                            <tbody class="carbon-quality-table-body">
                                <tr v-for="(issue, idx) in missingDataIssues" :key="idx">
                                    <td>
                                        <span class="table-cell-primary">{{
                                            issue.facilityName || issue.facilityCode || "-"
                                        }}</span>
                                    </td>
                                    <td>
                                        <span class="table-cell-primary">{{ issue.period }}</span>
                                    </td>
                                    <td>
                                        <span class="table-cell-primary">{{ issue.categoryCode }}</span>
                                    </td>
                                    <td>
                                        <span class="badge badge-danger">{{ issue.severity }}</span>
                                    </td>
                                    <td>
                                        <span class="table-cell-primary">{{ issue.reason }}</span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </section>

                <!-- 증빙 미첨부 내역 -->
                <section v-if="missingEvidence.length" class="detail-section compact-section">
                    <div class="section-header">
                        <h5 class="section-title">증빙 미첨부 내역</h5>
                        <span class="section-count">{{ missingEvidence.length }}건</span>
                    </div>
                    <div class="scroll-pane">
                        <table class="table table-sm table-striped compact-table">
                            <thead class="carbon-quality-table-header">
                                <tr>
                                    <th>시설</th>
                                    <th>기간</th>
                                    <th>사유</th>
                                    <th>조치</th>
                                </tr>
                            </thead>
                            <tbody class="carbon-quality-table-body">
                                <tr v-for="(item, idx) in missingEvidence" :key="idx">
                                    <td>
                                        <span class="table-cell-primary">{{ item.facilityIdx || "-" }}</span>
                                    </td>
                                    <td>
                                        <span class="table-cell-primary">{{ item.period }}</span>
                                    </td>
                                    <td>
                                        <span class="table-cell-primary">{{ item.reason }}</span>
                                    </td>
                                    <td>
                                        <span class="table-cell-primary">{{ item.suggestion }}</span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </section>

                <!-- 승인 대기 목록 -->
                <section v-if="pendingApprovals.length" class="detail-section compact-section">
                    <div class="section-header">
                        <h5 class="section-title">승인 대기 목록</h5>
                        <span class="section-count">{{ pendingApprovals.length }}건</span>
                    </div>
                    <div class="scroll-pane">
                        <table class="table table-sm table-striped compact-table">
                            <thead class="carbon-quality-table-header">
                                <tr>
                                    <th>시설</th>
                                    <th>기간</th>
                                    <th>대기일</th>
                                    <th>사유</th>
                                    <th>조치</th>
                                </tr>
                            </thead>
                            <tbody class="carbon-quality-table-body">
                                <tr v-for="(item, idx) in pendingApprovals" :key="idx">
                                    <td>
                                        <span class="table-cell-primary">{{ item.facilityIdx || "-" }}</span>
                                    </td>
                                    <td>
                                        <span class="table-cell-primary">{{ item.period }}</span>
                                    </td>
                                    <td>
                                        <span class="table-cell-primary">{{ item.pendingDays }}일</span>
                                    </td>
                                    <td>
                                        <span class="table-cell-primary">{{ item.reason }}</span>
                                    </td>
                                    <td>
                                        <span class="table-cell-primary">{{ item.suggestion }}</span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </section>
            </div>
        </div>
    </b-modal>
</template>

<script>
import CarbonCodes from "@/submodules/carbon-plugin/codes";

export default {
    name: "InventoryQualityModal",
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        result: {
            type: Object,
            default: null,
        },
    },
    computed: {
        statusLabel() {
            const status = this.result?.overallQuality || this.result?.status || "unknown";
            switch (status) {
                case "excellent":
                    return "우수";
                case "good":
                    return "양호";
                case "fair":
                    return "보통";
                case "poor":
                    return "미흡";
                default:
                    return "알 수 없음";
            }
        },
        statusClass() {
            const status = this.result?.overallQuality || this.result?.status;
            if (!status) return "quality-unknown";
            return `quality-${status}`;
        },
        scoreClass() {
            const score = this.result?.qualityScore ?? this.result?.overallScore ?? 0;
            if (score >= 90) return "score-excellent";
            if (score >= 70) return "score-good";
            if (score >= 50) return "score-fair";
            return "score-poor";
        },
        scoreDescription() {
            const score = this.result?.qualityScore ?? this.result?.overallScore ?? 0;
            if (score >= 90) {
                return "인벤토리 품질이 매우 우수합니다. 검증 준비가 잘 되어 있습니다.";
            }
            if (score >= 70) {
                return "인벤토리 품질이 양호합니다. 일부 개선 사항을 확인해주세요.";
            }
            if (score >= 50) {
                return "인벤토리 품질이 보통 수준입니다. 개선이 필요한 항목이 있습니다.";
            }
            return "인벤토리 품질이 미흡합니다. 즉시 조치가 필요합니다.";
        },
        actionItems() {
            return this.result?.summary?.recommendations || [];
        },
        missingDataIssues() {
            return this.extractFromCheck("missing_data", "missingDataList");
        },
        missingEvidence() {
            return this.extractFromCheck("evidence", "missingEvidenceList");
        },
        pendingApprovals() {
            return this.extractFromCheck("approval", "pendingList");
        },
    },
    methods: {
        handleHidden() {
            this.$emit("update:visible", false);
            this.$emit("close");
        },
        badgeClass(status) {
            switch (status) {
                case "pass":
                    return "badge-success";
                case "warning":
                    return "badge-warning";
                case "fail":
                    return "badge-danger";
                default:
                    return "badge-secondary";
            }
        },
        badgeLabel(status) {
            switch (status) {
                case "pass":
                    return "통과";
                case "warning":
                    return "경고";
                case "fail":
                    return "실패";
                default:
                    return status;
            }
        },

        extractFromCheck(category, field) {
            const check = (this.result?.checks || []).find((item) => item.category === category);
            return check?.details?.[field] || [];
        },
        priorityLabel(priority) {
            return CarbonCodes.severity.getLabel(priority) || priority || "정보";
        },
    },
};
</script>

<style scoped>
.quality-modal-body {
    background: #f6f7f9;
    padding: 0;
    max-height: 85vh;
    display: flex;
    flex-direction: column;
}

/* Loading & Empty States */
.loading-state,
.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    min-height: 300px;
    color: #64748b;
    font-size: 14px;
}

.loading-state i {
    font-size: 32px;
    color: #52b788;
}

.empty-state i {
    font-size: 48px;
    color: #cbd5e1;
}

.empty-state p {
    margin: 0;
    font-size: 14px;
}

/* Quality Content Layout */
.quality-content {
    display: flex;
    flex-direction: column;
    min-height: 0;
    max-height: 85vh;
}

/* Hero Section - 핵심 정보 영역 */
.hero-section {
    background: #ffffff;
    border-bottom: 1px solid #e5e7eb;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.hero-main-row {
    display: grid;
    grid-template-columns: 1fr auto auto;
    gap: 12px;
    align-items: start;
}

.hero-score-card {
    background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
    border: 1px solid #bbf7d0;
    border-radius: 10px;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 200px;
}

.hero-score-card.score-excellent {
    background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
    border-color: #bbf7d0;
}

.hero-score-card.score-good {
    background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
    border-color: #bfdbfe;
}

.hero-score-card.score-fair {
    background: linear-gradient(135deg, #fefce8 0%, #fef9c3 100%);
    border-color: #fde68a;
}

.hero-score-card.score-poor {
    background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
    border-color: #fecaca;
}

.hero-score-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.hero-label {
    font-size: 13px;
    font-weight: 700;
    color: #0f4a35;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.hero-status-badge {
    padding: 4px 12px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.3px;
}

.hero-status-badge.quality-excellent {
    background: #22c55e;
    color: #ffffff;
}

.hero-status-badge.quality-good {
    background: #3b82f6;
    color: #ffffff;
}

.hero-status-badge.quality-fair {
    background: #f59e0b;
    color: #ffffff;
}

.hero-status-badge.quality-poor {
    background: #ef4444;
    color: #ffffff;
}

.hero-score-value {
    display: flex;
    align-items: baseline;
    gap: 8px;
}

.hero-score-value strong {
    font-size: 36px;
    font-weight: 800;
    line-height: 1;
    color: #0f172a;
}

.hero-score-max {
    font-size: 16px;
    font-weight: 600;
    color: #64748b;
}

.hero-description {
    margin: 2px 0 0;
    font-size: 12px;
    font-weight: 500;
    color: #475569;
    line-height: 1.4;
}

.hero-stats-card,
.hero-methodology-card {
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 160px;
    height: 100%;
    justify-content: space-around;
}

.hero-stat-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.hero-stat-label {
    font-size: 13px;
    font-weight: 700;
    color: #0f172a;
    text-transform: uppercase;
    letter-spacing: 0.3px;
}

.hero-stat-count {
    font-size: 12px;
    font-weight: 600;
    color: #64748b;
}

.hero-stat-values {
    display: flex;
    gap: 12px;
}

.hero-stat-item {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 8px 4px;
    border-radius: 6px;
    background: #f6f7f9;
    transition: background-color 0.2s ease;
}

.hero-stat-item:hover {
    background: #e5e7eb;
}

.hero-stat-item strong {
    display: block;
    font-size: 20px;
    font-weight: 800;
    line-height: 1.2;
    margin-bottom: 2px;
}

.hero-stat-item.stat-pass strong {
    color: #16a34a;
}

.hero-stat-item.stat-warning strong {
    color: #f59e0b;
}

.hero-stat-item.stat-error strong {
    color: #ef4444;
}

.hero-stat-item span {
    font-size: 12px;
    font-weight: 600;
    color: #64748b;
}

.hero-methodology-list {
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.hero-methodology-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
}

.hero-methodology-item dt {
    font-size: 12px;
    font-weight: 700;
    color: #64748b;
    min-width: 70px;
    flex-shrink: 0;
    text-transform: uppercase;
    letter-spacing: 0.3px;
}

.hero-methodology-item dd {
    font-size: 13px;
    font-weight: 600;
    color: #0f172a;
    margin: 0;
    flex: 1;
}

/* Action Alert Inline */
.action-alert-inline {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
    border: 1px solid #fecaca;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    color: #991b1b;
}

.action-alert-inline i {
    font-size: 16px;
    color: #ef4444;
    flex-shrink: 0;
}

/* Detail Content - 스크롤 가능한 영역 */
.detail-content {
    flex: 1;
    overflow-y: auto;
    padding: 12px 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.detail-content::-webkit-scrollbar {
    width: 8px;
}

.detail-content::-webkit-scrollbar-track {
    background: #f6f7f9;
}

.detail-content::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
}

.detail-content::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
}

/* Detail Section */
.detail-section {
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.detail-section.compact-section {
    padding: 10px;
    gap: 8px;
}

.section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2px;
}

.section-title {
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
    letter-spacing: -0.01em;
}

.section-count {
    font-size: 12px;
    font-weight: 600;
    color: #64748b;
    padding: 2px 6px;
    background: #f6f7f9;
    border-radius: 10px;
}

/* Impact Grid */
.impact-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
}

.impact-card {
    padding: 10px 12px;
    border-radius: 8px;
    background: linear-gradient(135deg, rgba(82, 183, 136, 0.08) 0%, rgba(82, 183, 136, 0.04) 100%);
    border: 1px solid rgba(82, 183, 136, 0.15);
    display: flex;
    flex-direction: column;
    gap: 4px;
    transition: all 0.2s ease;
}

.impact-card:hover {
    background: linear-gradient(135deg, rgba(82, 183, 136, 0.12) 0%, rgba(82, 183, 136, 0.06) 100%);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(82, 183, 136, 0.1);
}

.impact-label {
    font-size: 12px;
    font-weight: 700;
    color: #0f4a35;
    text-transform: uppercase;
    letter-spacing: 0.3px;
}

.impact-value {
    font-size: 18px;
    font-weight: 800;
    color: #0f4a35;
}

/* Action Grid */
.action-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 10px;
}

.action-card {
    border-radius: 8px;
    padding: 10px 12px;
    border: 1px solid #e5e7eb;
    background: #ffffff;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    transition: all 0.2s ease;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.action-card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-1px);
}

.action-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 4px;
}

.priority-chip {
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.3px;
}

.priority-high .priority-chip {
    background: #fee2e2;
    color: #b91c1c;
}

.priority-medium .priority-chip {
    background: #fef3c7;
    color: #92400e;
}

.priority-low .priority-chip {
    background: #e0f2fe;
    color: #1d4ed8;
}

.action-category {
    font-size: 13px;
    font-weight: 700;
    color: #0f172a;
}

.action-text {
    font-size: 13px;
    font-weight: 600;
    color: #0f172a;
    margin: 2px 0 0;
    line-height: 1.4;
}

/* Table Styles */
.table-wrapper {
    width: 100%;
    overflow-x: auto;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.quality-table {
    margin: 0;
    border-collapse: separate;
    border-spacing: 0;
}

.carbon-quality-table-header {
    position: sticky;
    top: 0;
    background: linear-gradient(135deg, #f6f7f9 0%, #e5e7eb 100%);
    z-index: 10;
    text-align: center;
}

.carbon-quality-table-header th {
    font-size: 13px;
    font-weight: 700;
    color: #0f172a;
    padding: 12px 8px;
    text-transform: none;
    letter-spacing: 0;
    border-bottom: 2px solid #e5e7eb;
}

.carbon-quality-table-body {
    text-align: center;
}

.carbon-quality-table-body td {
    font-size: 13px;
    font-weight: 500;
    color: #334155;
    padding: 12px 8px;
    vertical-align: middle;
    border-bottom: 1px solid #f1f5f9;
}

.carbon-quality-table-body tr:hover {
    background-color: #f6f7f9;
    transition: background-color 0.2s ease;
}

.quality-table tbody tr:last-child td {
    border-bottom: none;
}

.table-cell-primary {
    font-size: 13px;
    font-weight: 600;
    color: #0f172a;
    line-height: 1.4;
}

.table-cell-secondary {
    font-size: 12px;
    font-weight: 500;
    color: #64748b;
    line-height: 1.3;
}

/* Badge Styles */
.badge {
    font-size: 11px;
    font-weight: 700;
    padding: 4px 10px;
    border-radius: 12px;
    display: inline-block;
    letter-spacing: 0.3px;
}

.badge-success {
    background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
    color: #166534;
    border: 1px solid #86efac;
}

.badge-warning {
    background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
    color: #92400e;
    border: 1px solid #fcd34d;
}

.badge-danger {
    background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
    color: #b91c1c;
    border: 1px solid #fca5a5;
}

.badge-secondary {
    background: linear-gradient(135deg, #f6f7f9 0%, #e5e7eb 100%);
    color: #475569;
    border: 1px solid #cbd5e1;
}

/* Scroll Pane */
.scroll-pane {
    max-height: 400px;
    overflow: auto;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.04);
}

.scroll-pane table {
    margin: 0;
}

.scroll-pane .carbon-quality-table-header {
    position: sticky;
    top: 0;
    z-index: 10;
}

.scroll-pane::-webkit-scrollbar {
    width: 8px;
    height: 8px;
}

.scroll-pane::-webkit-scrollbar-track {
    background: #f6f7f9;
    border-radius: 4px;
}

.scroll-pane::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
}

.scroll-pane::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
}

/* Responsive */
@media (max-width: 768px) {
    .hero-section {
        padding: 16px;
    }

    .hero-score-value strong {
        font-size: 36px;
    }
    .detail-content {
        padding: 16px;
    }
}
</style>
